






















































































































































import ConfirmActionDialog from "@/components/utility/ConfirmActionDialog.vue";
import Debug from "@/components/utility/Debug.vue";
import ErrorCard from "@/components/utility/ErrorCard.vue";
import LayoutSimple from "@/layouts/LayoutSimple.vue";
import TheLayoutPortal from "@/layouts/TheLayoutPortal.vue";
import { formatHoursAndMinutes } from "@/lib/utility/date-helper";
import { handleError } from "@/lib/utility/handleError";
import {
  BookingBookingInformationDtoGen,
  BookingCreateBookingWithoutResourceDtoGen,
  BookingCustomerInformationDtoGen,
  BookingSlotGen
} from "@/services/booking/v1/data-contracts";
import { AvailabilityModule } from "@/store/modules/availability.store";
import { PartnerModule } from "@/store/modules/partner";
import { Component, Vue, Watch } from "vue-property-decorator";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import LatestEntriesCardEmpty from "@/components/cards/LatestEntriesCardEmpty.vue";
import { requiredRule } from "@/lib/rules/requiredRule";

@Component({
  components: {
    LayoutSimple,
    TheLayoutPortal,
    ConfirmActionDialog,
    LatestEntriesCardEmpty,
    Debug,
    ErrorCard
  },
  filters: {
    formatHoursAndMinutes
  }
})
export default class BookingAvailability extends Vue {
  /**
   * Displays loading animation while async calls
   */
  isLoading = false;

  /**
   * Opening time booking dialog
   */
  isDialogActive = false;

  /**
   * Is dialog loading
   */
  isDialogLoading = false;

  /**
   * Is form valid
   */
  isValid = false;

  /**
   * The initial date displayed in the calender. Gets set by the first slot available in refresh.
   */
  // TODO: Test date:
  // date = new Date("2023-04-19").toISOString().substring(0, 10);
  date = new Date().toISOString().substring(0, 10);

  /**
   * v-model for the date when the user changes the month using the next and previous arrows.
   */
  pickerDate = null;

  bookingInformation: BookingBookingInformationDtoGen = {
    notes: ""
  };

  customerInformation: BookingCustomerInformationDtoGen = {
    lastName: "",
    firstName: "",
    email: "",
    numberPlate: ""
  };

  /**
   * The slot selected by the user
   */
  selectedSlot: BookingSlotGen | undefined = undefined;

  get partnerId() {
    return this.$route.params.partnerId || PartnerModule.partner.id;
  }

  get serviceId() {
    return this.$route.params.serviceId;
  }

  get booking() {
    return AvailabilityModule.booking;
  }

  get color() {
    return getDefaultPartnerColor();
  }

  get durationInMinutes() {
    if (!this.booking) {
      return 0;
    }

    return this.booking.durationInSeconds / 60;
  }

  get requiredRule() {
    return [requiredRule()];
  }

  get slots(): BookingSlotGen[] {
    return AvailabilityModule.booking?.slots || [];
  }

  get slotsAsDates(): string[] {
    // Return slots as the exact year-month-day (2023-04-13)
    return this.slots.map(slot => new Date(slot.startInSeconds * 1000).toISOString().substring(0, 10));
  }

  get slotsForDay(): BookingSlotGen[] {
    if (!this.slots) {
      return [];
    }

    // Slots for day should compare the hour as well
    const slotsForDay = this.slots.filter(slot => {
      return (
        new Date(slot.startInSeconds * 1000).toISOString().substring(0, 10) === this.date &&
        new Date(slot.startInSeconds * 1000) >= new Date()
      );
    });

    return slotsForDay;
  }

  @Watch("pickerDate")
  async fetchNewBookings(pickedMonth: string) {
    if (!isNaN(Date.parse(pickedMonth))) {
      const currentDate = new Date();
      const chosenDate = new Date(pickedMonth);

      /**
       * If we go back to the current month and year fetch with the current date(default),
       * send the chosen date otherwise
       * */
      if (chosenDate.getMonth() === currentDate.getMonth() && chosenDate.getFullYear() === currentDate.getFullYear()) {
        await this.refresh();
      } else {
        await this.refresh(chosenDate);
      }
    }
  }

  displayTimeSlot(slot: BookingSlotGen): Date {
    /**
     * old
     *
     * new Date(
     *  item.startInSeconds * 1000 + new Date(item.startInSeconds).getTimezoneOffset() * 60 * 1000
     * )
     */
    const slotTime = new Date(slot.startInSeconds * 1000);

    return slotTime;
  }

  slotSubtitle(): string | undefined {
    if (!this.selectedSlot) {
      return "";
    }

    return new Date(this.selectedSlot?.startInSeconds * 1000).toLocaleDateString("de-de", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric"
    });
  }

  /**
   * Returns true if date is within the ordered slots (0 lowest to length -1 highest).
   * @param val the date
   */
  allowedDates(val: string): boolean {
    if (!this.slots || this.slots.length < 1) {
      return false;
    }

    return this.slotsAsDates.includes(val);
  }

  selectSlot(item: BookingSlotGen) {
    this.$log.debug("selectSlot", item);
    this.selectedSlot = item;
    this.isDialogActive = true;
  }

  async bookSlot() {
    if (!this.isValid) {
      this.$toast.error("Not valid");
      throw new Error("not valid");
    }

    if (!this.selectedSlot) {
      this.$toast.error("No slot");
      throw new Error("no slot");
    }

    this.isDialogLoading = true;
    this.isLoading = true;
    try {
      const dto: BookingCreateBookingWithoutResourceDtoGen = {
        serviceId: this.serviceId,
        startInSeconds: this.selectedSlot.startInSeconds,
        durationInSeconds: this.selectedSlot.durationInSeconds,
        customerInformation: this.customerInformation,
        bookingInformation: this.bookingInformation
      };

      AvailabilityModule.create({ partnerId: this.partnerId, dto });

      await AvailabilityModule.create({ partnerId: this.partnerId, dto });
      // Close the dialog?
      this.isDialogActive = false;
      await this.refresh();
    } catch (error) {
      handleError(error);
    } finally {
      this.isLoading = false;
      // Close the dialog?`
      this.isDialogActive = false;
      this.isDialogLoading = false;
    }
  }

  async refresh(date?: Date) {
    this.isLoading = true;
    try {
      await AvailabilityModule.findOneByPartner({
        partnerId: this.partnerId,
        id: this.serviceId,
        // TODO: Test date. Remove date string. "2023-04-18"
        date: date?.toISOString() || new Date().toISOString()
      });

      this.date = new Date(this.slots[0].startInSeconds * 1000).toISOString().substring(0, 10);
    } catch (error) {
      handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  // async mounted() {
  //   await this.refresh();
  // }
}
